import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { BarChart3, ChevronUp, ChevronDown, Loader2, TrendingUp } from 'lucide-react';

// ── Monthly SLA scoreboard (leadership: fine exposure + Tier risk) ──
// Moved here (CRM › Dashboard › MFCWL tab) from the MFCWL ops queue page.
const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const BASE = `${API_URL}/b2b/mfcwl/admin`;

const SCORE_TIER = {
  ok:    { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'On track' },
  watch: { cls: 'bg-amber-100 text-amber-800 border-amber-300', label: 'Watch (1 more → Tier 2)' },
  tier2: { cls: 'bg-rose-100 text-rose-700 border-rose-300', label: 'Tier 2 risk' },
  tier4: { cls: 'bg-rose-600 text-white border-rose-700', label: 'Tier 4 — termination risk' },
};
const SCORE_LABEL = { submit: 'Inspection completion (API-2)', history: 'Service history', api4: 'Final send (API-4)' };

function slaMonthOptions() {
  const opts = []; const d = new Date();
  for (let i = 0; i < 6; i++) {
    const dt = new Date(d.getFullYear(), d.getMonth() - i, 1);
    opts.push({
      val: `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`,
      lab: dt.toLocaleString('en-IN', { month: 'short', year: 'numeric' }),
    });
  }
  return opts;
}

export default function SlaScoreboard({ defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  const months = useMemo(() => slaMonthOptions(), []);
  const [month, setMonth] = useState(months[0].val);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BASE}/sla-scoreboard`, { params: { month } });
      setData(res.data);
    } catch (e) { setData(null); }
    setLoading(false);
  }, [month]);

  useEffect(() => { if (open) load(); }, [open, load]);

  return (
    <div className="rounded-lg border bg-white" data-testid="mfcwl-sla-scoreboard">
      <button className="w-full flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              onClick={() => setOpen(o => !o)} data-testid="sla-scoreboard-toggle">
        <BarChart3 className="h-4 w-4 text-indigo-600" /> Monthly SLA scoreboard
        <span className="text-[11px] font-normal text-gray-400">— fine exposure &amp; Tier risk</span>
        {open ? <ChevronUp className="h-4 w-4 ml-auto" /> : <ChevronDown className="h-4 w-4 ml-auto" />}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3 border-t">
          <div className="flex items-center gap-2 pt-3">
            <label className="text-xs text-gray-500">Month</label>
            <select className="border rounded-md text-xs px-2 py-1 bg-white" value={month}
                    onChange={e => setMonth(e.target.value)} data-testid="sla-scoreboard-month">
              {months.map(o => <option key={o.val} value={o.val}>{o.lab}</option>)}
            </select>
            {loading && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
          </div>
          {data && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {['submit', 'history', 'api4'].map(k => {
                  const t = data.timers[k]; const tb = SCORE_TIER[t.tier_risk] || SCORE_TIER.ok;
                  return (
                    <div key={k} className="rounded-lg border p-3" data-testid={`sla-score-${k}`}>
                      <div className="text-[11px] font-medium text-gray-500">{SCORE_LABEL[k]}</div>
                      <div className="mt-1 flex items-end gap-4">
                        <div><div className="text-2xl font-bold text-rose-600" data-testid={`sla-score-${k}-breach`}>{t.breach}</div><div className="text-[10px] text-gray-400">breached</div></div>
                        <div><div className="text-2xl font-bold text-amber-600">{t.near_miss}</div><div className="text-[10px] text-gray-400">near-miss</div></div>
                      </div>
                      <span className={`inline-block mt-2 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${tb.cls}`}>{tb.label}</span>
                    </div>
                  );
                })}
              </div>
              <div className="text-[11px] text-gray-500 flex items-start gap-1.5">
                <TrendingUp className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span><b>{data.totals.breach}</b> breaches &amp; <b>{data.totals.near_miss}</b> near-misses across {data.totals.inspections} inspections this month.
                <span className="text-gray-400"> SOW escalation: 3 same-type breaches → Tier 2 (written notice) · 5 → Tier 4 (termination).</span></span>
              </div>
            </>
          )}
          {!loading && !data && <div className="text-xs text-gray-400" data-testid="sla-scoreboard-empty">No scoreboard data for this month.</div>}
        </div>
      )}
    </div>
  );
}
